import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import { AgentModel } from './agent.model';
import type { ActionTrackingContext } from '../actions/framework/action-tracking';
import { ChatModel } from '../chat';

type Params = {
  message: string;
};

export class SubmitChatMessageAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Submit Chat Message';
  description = 'Submit a message in the agent chat';
  inputSchema = z.object({
    message: z.string(),
  });
  discrete = true;
  group = 'Chat';
  icon = 'paper-airplane';

  applicable(context: ApplicableContext): boolean {
    const chatModel = context.targetedModelOfType(ChatModel);
    return chatModel !== null && chatModel.inputWatchable.snapshot().length > 0;
  }

  async execute(
    _: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const agent = context.targetedModelOfType(AgentModel);

    if (agent === null) {
      return ok({});
    }

    const hasMessages = agent.hasMessages.snapshot();
    const currentTab = agent.currentTab.snapshot();

    // If this is the first message on the chat tab, trigger animation and delay
    if (!hasMessages && currentTab === 'chat') {
      agent.hasMessages.set(true);
      // Wait for animation to start before allowing message to send
      await new Promise((resolve) => setTimeout(resolve, 250));
    } else if (!hasMessages) {
      // On other tabs, just set hasMessages without delay
      agent.hasMessages.set(true);
    }

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const agent = context.targetedModelOfType(AgentModel);
    if (agent === null) {
      return null;
    }

    const promptInput = agent.promptInput.snapshot();
    const trimmedLength = payload.message.trim().length;

    return {
      event: 'agent_chat_message_submitted',
      properties: {
        agentId: agent.id,
        agentName: agent.name.snapshot(),
        messageLength: trimmedLength,
        hasContent: trimmedLength > 0,
        toolCount: promptInput.toolIds.length,
        model: promptInput.model,
        tab: agent.currentTab.snapshot(),
      },
    };
  }
}
